import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TagModule } from 'primeng/tag';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import { EInvoiceProviderService } from '../provider/services/e-invoice-provider.service';

import {
  HISTORY_DEFAULT_COLUMNS,
  HISTORY_FLEXIBLE_COLUMNS,
  HISTORY_SEARCH_CONFIG,
  formatCurrency,
  getInvoiceStatusDisplayText,
  getInvoiceStatusSeverity,
  getPaymentMethodDisplayText,
  getTaxSubmissionDisplayText,
  getTaxSubmissionSeverity,
} from './e-invoice-history.config';
import {
  EInvoiceHistory,
  EInvoiceHistoryPageRequest,
  EInvoiceHistoryService,
} from './services/e-invoice-history.service';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';
import { FormsModule } from '@angular/forms';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';

@Component({
  selector: 'app-e-invoice-history',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    DialogModule,
    ButtonModule,
    FormsModule,
    TagModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
  ],
  providers: [MessageService],
  templateUrl: './e-invoice-history.component.html',
  styleUrls: [
    '../shared/e-invoice-table.css',
    './e-invoice-history.component.css',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EInvoiceHistoryComponent implements OnInit, OnDestroy {
  // Pagination
  first = 0;
  rows = 20;

  // Configuration
  searchFormConfig = signal<FormFieldConfig[]>([...HISTORY_SEARCH_CONFIG]);
  readonly defaultColumns: TableColumn[] = HISTORY_DEFAULT_COLUMNS;
  readonly flexibleColumns: TableColumn[] = HISTORY_FLEXIBLE_COLUMNS;

  // Data signals
  dataSource = signal<EInvoiceHistory[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal<EInvoiceHistoryPageRequest>({
    page: 0,
    size: 20,
  });

  // Button configuration — no "New" button for history (read-only)
  readonly primaryButton: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: false,
    icon: 'add',
  };

  readonly secondaryButton: ActionButton = {
    label: '',
    severity: 'success',
    outlined: true,
    show: true,
    icon: 'view_column',
  };

  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // PDF preview state
  pdfDialogVisible = signal(false);
  pdfUrl = signal<SafeResourceUrl | null>(null);
  pdfLoading = signal(false);

  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly historyService = inject(EInvoiceHistoryService);
  private readonly messageService = inject(MessageService);
  private readonly providerService = inject(EInvoiceProviderService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly sanitizer = inject(DomSanitizer);
  private pdfBlobUrl: string | null = null;

  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('e-invoice.e_invoice_history.view');
  });

  ngOnInit(): void {
    this.searchFormConfig.set(
      withPaginatedSearchFields(
        HISTORY_SEARCH_CONFIG.map((field) => ({
          ...field,
          label: this.translateService.instant(field.label),
          placeholder: field.placeholder
            ? this.translateService.instant(field.placeholder)
            : field.placeholder,
          options: field.options?.map((opt) => ({
            ...opt,
            label: this.translateService.instant(opt.label),
          })),
        })),
        {
          providerId: {
            loadPage: (params) =>
              this.providerService.getProviderOptionsPage(params),
          },
        },
      ),
    );
    this.loadInvoices();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokePdfUrl();
  }

  private revokePdfUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  private loadInvoices(): void {
    this.loading.set(true);

    this.historyService
      .searchInvoices(this.searchParams())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.pageContent || []);
          this.totalRecords.set(response.totalElements || 0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.history.messages.error.failed_to_load',
            ),
          });
        },
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;
    this.searchParams.set({
      invoiceNo: (searchData?.['invoiceNo'] as string) || undefined,
      orderNo: (searchData?.['orderNo'] as string)?.trim() || undefined,
      issuedFrom: searchData?.['issuedFrom']
        ? new Date(searchData['issuedFrom'] as string).toISOString()
        : undefined,
      issuedTo: searchData?.['issuedTo']
        ? new Date(searchData['issuedTo'] as string).toISOString()
        : undefined,
      status: (searchData?.['status'] as string) || undefined,
      companyId:
        searchData?.['companyId'] != null
          ? Number(searchData['companyId'])
          : undefined,
      providerId:
        searchData?.['providerId'] != null
          ? Number(searchData['providerId'])
          : undefined,
      page: 0,
      size: this.rows,
    });
    this.loadInvoices();
  }

  onReset(): void {
    this.first = 0;
    this.searchParams.set({
      page: 0,
      size: this.rows,
    });
    this.loadInvoices();
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.searchParams.set({
      ...this.searchParams(),
      page: event.first / event.rows,
      size: event.rows,
    });
    this.loadInvoices();
  }

  // Display helpers
  getStatusSeverity(status: string): string {
    return getInvoiceStatusSeverity(status);
  }

  getStatusDisplayText(status: string): string {
    return getInvoiceStatusDisplayText(status, this.translateService);
  }

  getTaxSubmissionSeverity(status: string): string {
    return getTaxSubmissionSeverity(status);
  }

  getTaxSubmissionDisplayText(status: string): string {
    return getTaxSubmissionDisplayText(status, this.translateService);
  }

  getPaymentMethodDisplayText(paymentMethod: string): string {
    return getPaymentMethodDisplayText(paymentMethod, this.translateService);
  }

  formatCurrency(value: number | null | undefined): string {
    return formatCurrency(value);
  }

  formatDateTime(dateInput: any): string {
    return formatDateTime(dateInput);
  }

  getEmptyDisplay(value: any): string {
    return value || '--';
  }

  onView(invoice: EInvoiceHistory): void {
    this.pdfLoading.set(true);
    this.pdfDialogVisible.set(true);
    this.revokePdfUrl();

    this.historyService
      .downloadInvoicePdf(invoice)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.pdfBlobUrl = URL.createObjectURL(blob);
          this.pdfUrl.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl),
          );
          this.pdfLoading.set(false);
        },
        error: () => {
          this.pdfLoading.set(false);
          this.pdfDialogVisible.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.history.messages.error.download_failed',
            ),
          });
        },
      });
  }

  closePdfDialog(): void {
    this.pdfDialogVisible.set(false);
    this.revokePdfUrl();
    this.pdfUrl.set(null);
  }

  onDownload(invoice: EInvoiceHistory): void {
    this.historyService
      .downloadInvoicePdf(invoice)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `invoice_${invoice.invoiceNumber || invoice.id}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.history.messages.error.download_failed',
            ),
          });
        },
      });
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
